import { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { COUNTRY_CODES } from '@/lib/phone';

/**
 * Compact country dial-code picker. The collapsed control shows only the dial
 * code (e.g. "+91"); the open list shows the country + code (e.g. "IN +91") so
 * the user can tell which country a code belongs to. Empty until one is picked:
 * no country is ever assumed. Used before the phone input on the Add / Edit
 * customer forms.
 */
export function CountryCodeSelect({
  value,
  onChange,
}: {
  value: string;
  onChange: (code: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative flex-shrink-0" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label="Country code"
        className="flex items-center gap-1 px-2 py-2 w-[4.5rem] justify-between border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
      >
        {value ? <span>{value}</span> : <span className="text-gray-400">Code</span>}
        <ChevronDown className="h-4 w-4 text-gray-400" />
      </button>
      {open && (
        <ul className="scrollbar-hide absolute z-20 mt-1 max-h-60 w-28 overflow-auto rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-lg py-1">
          {COUNTRY_CODES.map((c) => (
            <li key={c.code}>
              <button
                type="button"
                onClick={() => {
                  onChange(c.code);
                  setOpen(false);
                }}
                className={`flex w-full justify-between px-3 py-1.5 text-sm hover:bg-gray-100 dark:hover:bg-gray-600 ${
                  c.code === value ? 'font-semibold text-primary-600 dark:text-primary-400' : 'text-gray-700 dark:text-gray-200'
                }`}
              >
                <span>{c.country}</span>
                <span>{c.code}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
